import {
    ChangeDetectionStrategy,
    Component,
    computed,
    linkedSignal,
    DestroyRef,
    ElementRef,
    inject,
    input,
    output,
} from '@angular/core';
import {
    takeUntilDestroyed,
    toObservable,
    toSignal,
} from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { injectViewportBreakpoints } from '@quipex/core/utils';
import {
    HERO_ICONS,
    QpxButton,
    QpxDateTimePipes,
    QpxTypography,
} from '@quipex/shared/ui';
import { delay, distinctUntilChanged, interval, map } from 'rxjs';
import { isCanonicalDocumentId } from '../../../runtime';
import type {
    QuippyAssistantAppearance,
    QuippyDocumentOption,
    QuippyMessage,
} from '../../../runtime';
import { QuippyMessageItem } from '../message';

@Component({
    selector: 'qpx-quippy-thread',
    imports: [
        QuippyMessageItem,
        MatButtonModule,
        QpxButton,
        QpxDateTimePipes,
        QpxTypography,
    ],
    templateUrl: './thread.html',
    styleUrls: ['./thread.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        role: 'log',
        'aria-live': 'polite',
        'aria-label': 'Chat messages',
    },
})
export class QuippyThread {
    //#region Dependency injection
    private readonly elementRef = inject(ElementRef<HTMLElement>);
    private readonly destroyRef = inject(DestroyRef);
    //#endregion

    //#region Inputs
    public readonly chatId = input.required<string | null>();
    public readonly appearance = input<QuippyAssistantAppearance>('elevated');
    public readonly messages = input.required<readonly QuippyMessage[]>();
    public readonly isThreadLoading = input.required<boolean>();
    public readonly sourceFallbackBuildingId = input<string | null>(null);
    public readonly allowPackDiscovery = input(false);
    //#endregion

    protected readonly isPhone = injectViewportBreakpoints().isPhone;
    protected readonly welcomeImage = computed(() =>
        this.appearance() === 'outlined' || this.isPhone()
            ? HERO_ICONS.quippy.sm
            : HERO_ICONS.quippy.md
    );
    protected readonly conversationMessages = computed(() =>
        this.messages().filter((message) => message.kind !== 'intro')
    );
    protected readonly welcomeMessage = computed(() =>
        this.conversationMessages().length === 0
            ? this.messages().find((message) => message.kind === 'intro')
            : undefined
    );

    //#region Outputs
    public readonly preparePack = output<string>();
    public readonly retry = output<QuippyMessage>();
    public readonly editResend = output<{
        message: QuippyMessage;
        content: string;
    }>();
    public readonly documentOptionSelected = output<{
        message: QuippyMessage;
        option: QuippyDocumentOption;
    }>();
    //#endregion

    //#region State
    protected readonly now = toSignal(
        interval(15 * 60 * 1000).pipe(map(() => new Date())),
        { initialValue: new Date() }
    );
    protected readonly packOfferTimestamp = computed(
        () => this.messages().at(-1)?.timestamp
    );
    protected readonly packOfferDismissed = linkedSignal({
        source: this.chatId,
        computation: () => false,
    });
    protected readonly showPackOffer = computed(() => {
        if (
            !this.allowPackDiscovery() ||
            this.isThreadLoading() ||
            this.packOfferDismissed()
        )
            return false;
        const buildingId = this.sourceFallbackBuildingId();
        if (!isCanonicalDocumentId(buildingId)) return false;
        const messages = this.messages();
        if (messages.some((message) => message.packAction)) return false;
        const firstResult = messages.find(
            (message) =>
                message.role === 'assistant' &&
                message.kind === 'message' &&
                message.status === 'sent' &&
                !message.documentOptions?.length &&
                message.sources.length > 0 &&
                message.sources.length <= 50 &&
                message.sources.every(
                    (source) =>
                        isCanonicalDocumentId(source.id) &&
                        (source.buildingId ?? buildingId) === buildingId
                )
        );
        return Boolean(firstResult && firstResult === messages.at(-1));
    });
    //#endregion

    //#region Template methods
    protected onPreparePack(): void {
        if (!this.showPackOffer()) return;
        this.packOfferDismissed.set(true);
        this.preparePack.emit(
            'Prepare a document pack using all the documents in your most recent response. Show me the proposed sections, requirements and assigned documents for review before creating it.'
        );
    }
    //#endregion

    //#region Lifecycle hooks
    constructor() {
        toObservable(this.chatId)
            .pipe(
                distinctUntilChanged(),
                delay(0),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(() => {
                this.scrollToBottom();
            });

        toObservable(this.messages)
            .pipe(delay(0), takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.scrollToBottom());
    }
    //#endregion

    //#region Private methods
    private scrollToBottom(): void {
        const element = this.elementRef.nativeElement;
        element.scrollTo({
            top: this.welcomeMessage() ? 0 : element.scrollHeight,
            behavior: 'smooth',
        });
    }
    //#endregion
}
